import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RewardType } from '~/shared';

export interface RewardFormData {
	id: string;
	type: RewardType;
	value: string;
}

export interface PromoCodeFormData {
	code: string;
	dateStart: string;
	dateEnd: string;
	requiredPlayTimeMin: number;
	maxActivations: string;
	rewards: RewardFormData[];
}

export interface ValidationError {
	field: string;
	message: string;
}

type PromoView = 'add' | 'list';

interface PromoState {
	currentView: PromoView;
	formData: PromoCodeFormData;
	validationErrors: ValidationError[];
	isSubmitting: boolean;
}

const createEmptyReward = (): RewardFormData => ({
	id: Math.random().toString(36).substr(2, 9),
	type: RewardType.MONEY,
	value: '',
});

const initialState: PromoState = {
	currentView: 'add',
	formData: {
		code: '',
		dateStart: '',
		dateEnd: '',
		requiredPlayTimeMin: 0,
		maxActivations: '',
		rewards: [],
	},
	validationErrors: [],
	isSubmitting: false,
};

const promoSlice = createSlice({
	name: 'promo',
	initialState,
	reducers: {
		setCurrentView: (state, action: PayloadAction<PromoView>) => {
			state.currentView = action.payload;
		},
		setCode: (state, action: PayloadAction<string>) => {
			state.formData.code = action.payload;
		},
		setDateStart: (state, action: PayloadAction<string>) => {
			state.formData.dateStart = action.payload;
		},
		setDateEnd: (state, action: PayloadAction<string>) => {
			state.formData.dateEnd = action.payload;
		},
		setRequiredPlayTimeMin: (state, action: PayloadAction<number>) => {
			state.formData.requiredPlayTimeMin = action.payload;
		},
		setMaxActivations: (state, action: PayloadAction<string>) => {
			state.formData.maxActivations = action.payload;
		},
		addReward: (state) => {
			state.formData.rewards.push(createEmptyReward());
		},
		removeReward: (state, action: PayloadAction<string>) => {
			state.formData.rewards = state.formData.rewards.filter(
				(r) => r.id !== action.payload,
			);
			// Allow empty rewards list - don't auto-create new reward
		},
		updateReward: (
			state,
			action: PayloadAction<{
				id: string;
				field: keyof RewardFormData;
				value: string;
			}>,
		) => {
			const { id, field, value } = action.payload;
			const reward = state.formData.rewards.find((r) => r.id === id);
			if (reward) {
				if (field === 'type') {
					reward.type = value as RewardType;
					reward.value = '';
				} else {
					(reward as any)[field] = value;
				}
			}
		},
		setValidationErrors: (state, action: PayloadAction<ValidationError[]>) => {
			state.validationErrors = action.payload;
		},
		setIsSubmitting: (state, action: PayloadAction<boolean>) => {
			state.isSubmitting = action.payload;
		},
	},
});

export const {
	setCurrentView,
	setCode,
	setDateStart,
	setDateEnd,
	setRequiredPlayTimeMin,
	setMaxActivations,
	addReward,
	removeReward,
	updateReward,
	setValidationErrors,
	setIsSubmitting,
} = promoSlice.actions;

export default promoSlice.reducer;
